import {
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  SelectProps,
} from '@mui/material';
import { FastField, FieldProps, getIn } from 'formik';
import React from 'react';
import { Stack } from 'src/components/Stack';
import { FormFieldWrapper } from 'src/shared/Forms/FormFieldWrapper';

export interface Option {
  value: string;
  label: string;
}

export interface FormSelectProps extends Omit<SelectProps, 'name' | 'value'> {
  name: string;
  options: Option[];
  label?: string;
  helperText?: string;
  prompt?: string;
  /**
   * Fast is meant for performance optimization. However, you really do not need to use it until you do. Only proceed if you are familiar with how React's shouldComponentUpdate() works. You have been warned.
   * No. Seriously. Please review the following parts of the official React documentation before continuing
   * @see {@link https://legacy.reactjs.org/docs/react-component.html#shouldcomponentupdate | React shouldComponentUpdate() Reference}
   * @see {@link https://legacy.reactjs.org/docs/optimizing-performance.html#shouldcomponentupdate-in-action | React shouldComponentUpdate in Action}
   */
  fast?: boolean;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  name,
  options,
  label,
  helperText,
  size,
  prompt,
  fast,
  ...props
}) => {
  const promptLabel = prompt ? <MenuItem value={''}>{prompt}</MenuItem> : null;

  const renderSelect = (
    field: any,
    showError: boolean,
    onChange?: (e: any) => void
  ) => (
    <Select
      {...field}
      {...props}
      onChange={onChange}
      error={showError}
      size={size || 'small'}
      fullWidth
      sx={{ py: 1 }}
    >
      {promptLabel}
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );

  if (fast) {
    return (
      <FastField name={name}>
        {({ field, meta, form }: FieldProps) => {
          const error = getIn(form.errors, name);
          const touched = getIn(form.touched, name);
          const showError = Boolean(error && touched);

          return (
            <Stack direction="column" width="100%">
              {label && <FormLabel>{label}</FormLabel>}

              {renderSelect(field, showError, (e) => {
                field.onChange(e);
                props.onChange?.(e, null);
              })}

              {(helperText || showError) && (
                <FormHelperText>
                  {showError ? meta.error : helperText}
                </FormHelperText>
              )}
            </Stack>
          );
        }}
      </FastField>
    );
  }
  return (
    <FormFieldWrapper name={name} label={label} helperText={helperText}>
      {({ field, form }: FieldProps) => {
        const error = getIn(form.errors, name);
        const touched = getIn(form.touched, name);
        const showError = Boolean(error && touched);

        return renderSelect(field, showError, (e) => {
          field.onChange(e);
          props.onChange?.(e, null);
        });
      }}
    </FormFieldWrapper>
  );
};
